import { useEffect, useState } from "react";
import { Check, Zap, Award } from "lucide-react";

const PromotionsSection = () => {
  const [timeLeft, setTimeLeft] = useState({
    hours: 17,
    minutes: 16,
    seconds: 40,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (val: number) => val.toString().padStart(2, "0");

  return (
    <section className="py-10 px-4 bg-[#e8edf2]">
      <div className="container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* Card 1: Daikin Promotion */}
          <div className="rounded-xl overflow-hidden shadow-lg bg-[#006e8a] text-white flex flex-col">
            <div className="p-8 flex-1">
              {/* Daikin Logo */}
              <div className="flex items-center gap-2 mb-6">
                <div className="flex items-center">
                  <div className="w-0 h-0 border-l-[20px] border-l-white border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent mr-1"></div>
                  <span className="text-2xl font-bold tracking-tight">DAIKIN</span>
                </div>
                <span className="text-xs font-medium uppercase tracking-wide border-l border-white/40 pl-2">
                  Authorised<br />Dealer
                </span>
              </div>

              {/* Main Offer */}
              <div className="mb-2">
                <p className="text-sm mb-1">Up to</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-8xl font-bold leading-none">30</span>
                  <div className="flex flex-col">
                    <span className="text-3xl font-bold">% OFF</span>
                  </div>
                </div>
                <p className="text-lg mt-3">Selected Daikin Range Units</p>
              </div>

              {/* Badge */}
              <div className="flex items-end justify-between mt-4">
                <div>
                  <p className="text-sm font-medium mt-2">Daikin Specials & Promotions</p>
                  <p className="text-xs opacity-100">* Special Conditions Apply</p>
                </div>
                <div className="relative w-20 h-20 flex-shrink-0">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle cx="50" cy="50" r="45" fill="none" stroke="white" strokeWidth="2" strokeDasharray="4 2" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <Award className="w-4 h-4 mb-1" />
                    <span className="text-[9px] leading-tight">Australia's<br />No.1<br />Daikin Dealer</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Separate Button */}
            <div className="px-8 pb-8">
              <a
                href="/specials"
                className="block w-full text-center bg-[#b8450e] hover:bg-[#a03c0c] text-white font-bold py-4 px-6 rounded-lg transition-colors text-lg uppercase tracking-wide"
              >
                See Daikin Deals
              </a>
            </div>
          </div>

          {/* Card 2: Rinnai Spring Sale */}
          <div className="rounded-xl overflow-hidden shadow-lg flex flex-col relative">
            {/* Background */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1522383225653-ed111181a951?w=600&h=500&fit=crop')`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/85 to-white/95"></div>
            </div>

            <div className="relative p-8 flex-1 flex flex-col">
              {/* Header */}
              <div className="mb-4">
                <span className="bg-[#E60012] text-white px-4 py-1.5 rounded text-lg font-bold inline-block">Rinnai</span>
              </div>

              {/* Spring Sale */}
              <div className="mb-4">
                <p className="text-[#0099CC] font-bold text-sm uppercase tracking-wider">UNBEATABLE</p>
                <h3 className="text-4xl font-bold">
                  <span className="text-[#FF6B9D]">Spring</span>{" "}
                  <span className="text-[#E60012]">Sale</span>
                </h3>
              </div>

              {/* Product Info */}
              <div className="mb-4">
                <div className="inline-flex items-center gap-1 bg-[#333] text-white px-3 py-1.5 rounded text-xs mb-3">
                  <Zap className="w-3 h-3" />
                  <span className="font-semibold">DUCTED SYSTEM 7.5 KW</span>
                </div>
                <div className="text-5xl font-bold text-[#E60012]">
                  $5,999<span className="text-2xl align-top">*</span>
                </div>
              </div>

              {/* Features */}
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-700 mb-4">
                <div className="flex items-center gap-1">
                  <Check className="w-3 h-3 text-[#E60012] flex-shrink-0" />
                  <span>7 Year Warranty</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-3 h-3 text-[#E60012] flex-shrink-0" />
                  <span>Energy Rebate Qualified</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-3 h-3 text-[#E60012] flex-shrink-0" />
                  <span>Wi-Fi Included</span>
                </div>
                <div className="flex items-center gap-1">
                  <Check className="w-3 h-3 text-[#E60012] flex-shrink-0" />
                  <span>Valid till 30th November</span>
                </div>
              </div>

              {/* Countdown */}
              <div className="mt-auto pt-4 border-t border-gray-200 text-center">
                <p className="text-sm font-medium text-gray-700">
                  Limited-Time Only!{" "}
                  <span className="text-[#E60012] font-bold text-lg">
                    {formatTime(timeLeft.hours)}:{formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
                  </span>
                </p>
              </div>
            </div>

            {/* Separate Button */}
            <div className="relative px-8 pb-8">
              <a
                href="/brands/rinnai"
                className="block w-full text-center bg-[#E60012] hover:bg-[#cc0010] text-white font-bold py-4 px-6 rounded-lg transition-colors text-lg uppercase tracking-wide"
              >
                View Rinnai Deals
              </a>
            </div>
          </div>

          {/* Card 3: Zip Money */}
          <div className="rounded-xl overflow-hidden shadow-lg bg-[#1a0a2e] text-white p-8 flex flex-col">
            <div className="flex-1">
              <p className="text-lg mb-1">
                Up to <span className="text-[#b8a0ff] font-bold">60 months</span>
              </p>
              <p className="text-xl font-bold mb-1">interest free with</p>
              <p className="text-2xl font-bold">
                Zip Money<span className="text-sm">*</span>
              </p>
              <p className="text-xs text-gray-400 mt-2">*T&Cs apply</p>
            </div>

            {/* Zip Logo */}
            <div className="mt-4 flex justify-center">
              <img src="/zip-logo.png" alt="Zip Money interest-free payment provider logo" className="w-full max-w-[280px] object-contain" width={280} height={80} loading="lazy" />
            </div>

            {/* Separate Button */}
            <div className="mt-4">
              <a
                href="#"
                className="block w-full text-center bg-[#7355d4] hover:bg-[#6548b8] text-white font-bold py-3 px-6 rounded-lg transition-colors text-sm uppercase tracking-wide"
              >
                Learn More
              </a>
            </div>
          </div>

          {/* Card 4: Sponsors */}
          <div className="rounded-xl overflow-hidden shadow-lg bg-[#003B5C] text-white p-6 flex flex-col">
            <div className="mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white/20 rounded flex items-center justify-center border border-white/30">
                  <span className="text-white font-bold text-[10px]">ACG</span>
                </div>
                <span className="text-xs text-gray-300">ACG Southern Highlands</span>
                <span className="text-gray-500 mx-1">|</span>
                <span className="text-sm font-bold">Proud Sponsors Of</span>
              </div>
              <p className="text-sm mt-2">
                <span className="text-[#FF80AE]">the children's</span>{" "}
                <span className="text-white font-semibold">hospital</span>{" "}
                <span className="text-gray-300 text-xs">at Westmead</span>
              </p>
            </div>

            <div className="flex items-center justify-center flex-1">
              <img
                src="/childrens-hospital.webp"
                alt="ACG Southern Highlands proudly sponsors Sydney Children's Hospitals Foundation"
                className="w-full h-auto rounded object-contain"
                width={784}
                height={521}
                loading="lazy"
              />
            </div>
            <p className="text-center text-sm text-gray-300 mt-3 leading-snug">
              ACG Proudly Supports<br />Sydney Children's Hospitals Foundation
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PromotionsSection;
